import { useCallback, useEffect, useState } from 'react';

import { useSignInDialogStore } from '@/features/auth/signInDialogStore';
import { useBrowserWalletStore } from '@/features/browserWallet/store';
// the store module only: the feature barrel also carries the flow's dialogs,
// and the sign-out one imports the logout routine.
import { useFlowStore } from '@/features/flow/store';
import {
    clearLocalAuthentication,
    performAccountLogout,
} from '@/features/tradingSession/logout';
import { useTradingSessionStore } from '@/features/tradingSession/store';
import { describeErrorText } from '@/lib/errors';

import { deriveRuntimeMode } from '../authPolicy';
import { isSessionExpired, useWalletStore } from '../store';

/**
 * Application-token and user-authority orchestration.
 *
 * Real mode requires a valid Ed25519 app token plus either OAuth authority or
 * an active EIP-712/passkey trading window. A connected wallet is deliberately
 * not a runtime requirement; it is only used for EIP onboarding/reauth and
 * deposit steps.
 *
 * Flow-aware (plan 2026-10-04 §2): while the browser wallet flow (V2) is in
 * use, every V1 credential reads as absent (`isAuthenticated`,
 * `hasTradingAuthority` false, `token` null), so no V1 client exists and
 * nothing V1 talks to the backend; real mode then means "the browser wallet is
 * unlocked". `sessionKey` is what the trading store keys on: the app token in
 * V1, `v2:<address>` in V2 while unlocked, else null. It is an enable gate and
 * a query identity only; nothing sends it over the wire in V2.
 */
export function useAuth() {
    const session = useWalletStore((state) => state.session);
    const runtimeMode = useWalletStore((state) => state.runtimeMode);
    const setRuntimeMode = useWalletStore((state) => state.setRuntimeMode);
    const status = useTradingSessionStore((state) => state.status);
    const clockNowMs = useTradingSessionStore((state) => state.clockNowMs);
    const openSignIn = useSignInDialogStore((state) => state.open);
    const isV2 = useFlowStore((state) => state.active === 'v2');
    const browserSignerAddress = useBrowserWalletStore((state) =>
        isV2 ? (state.signer?.address ?? null) : null,
    );

    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);

    const hasAppToken = !!session && !isSessionExpired(session, clockNowMs);
    const hasV1Authority = useTradingSessionStore((state) =>
        state.hasActiveAuthority(clockNowMs),
    );
    const isAuthenticated = !isV2 && hasAppToken;
    const hasTradingAuthority = !isV2 && hasV1Authority;
    const derivedMode = isV2
        ? browserSignerAddress
            ? 'real'
            : 'demo'
        : deriveRuntimeMode(isAuthenticated, hasTradingAuthority);

    useEffect(() => {
        setRuntimeMode(derivedMode);
    }, [derivedMode, setRuntimeMode]);

    const login = useCallback(async () => {
        setAuthError(null);
        openSignIn();
    }, [openSignIn]);

    const logout = useCallback(async () => {
        setIsAuthenticating(true);
        setAuthError(null);
        try {
            await performAccountLogout();
        } catch (error) {
            setAuthError(describeErrorText(error, { operation: 'Sign-out' }));
            throw error;
        } finally {
            setIsAuthenticating(false);
        }
    }, []);

    const clearLocalSession = useCallback(() => {
        clearLocalAuthentication();
        setAuthError(null);
    }, []);

    const token = isAuthenticated && session ? session.token : null;

    return {
        runtimeMode,
        isAuthenticated,
        hasTradingAuthority,
        authorityMethod: isV2 ? null : (status?.method ?? null),
        isAuthenticating,
        login,
        logout,
        clearLocalSession,
        authError,
        token,
        sessionKey: isV2
            ? browserSignerAddress
                ? `v2:${browserSignerAddress}`
                : null
            : token,
    };
}
